import { FC, ReactNode, useContext, useMemo } from 'react';
import { ref } from 'valtio';
import { Timestamp } from 'firebase/firestore';
import { PageScopeContext, usePageScopeProxy, type UseEntityRecordStoreReturn } from '@/store';
import { LayoutEditorContext } from './LayoutEditorContext';
import { RecordStatus } from '#types/System.Parameters.types';
import type { Entity, EntityRecord, Meta } from '#types/System.types';

const noop = () => {};
const noopAsync = async () => {};

/**
 * Builds a mocked `useEntityRecordScope()` value pointing at the layout
 * currently being edited. Used by the layout editor's preview path so the
 * preview renders against deterministic fake data instead of the live store.
 */
const createPreviewScope = (editorLayout: Entity['defaultUILayout']): UseEntityRecordStoreReturn => {
    const previewMeta = {
        created: Timestamp.now(),
        lastModified: Timestamp.now(),
    } as Meta;

    const previewEntity: Entity = {
        defaultUILayout: editorLayout,
        description: {
            shortLabel: 'Preview Entity',
            shortDescription: 'Preview Mode',
        },
        status: RecordStatus.Active,
        meta: previewMeta,
    };

    const previewRecord: EntityRecord = {
        name: 'Preview Record',
        fields: {},
        entityReferences: {},
        status: RecordStatus.Active,
        meta: previewMeta,
    };

    return {
        entityId: '__preview__',
        recordId: '__preview__',
        entityGroupId: undefined,
        entityLocked: true,
        insideEntityRecordContext: true,
        entity: { data: previewEntity, loading: false, error: null },
        entityRecord: { data: previewRecord, loading: false, error: null },
        setEntityRecord: noopAsync,
        debouncedSetEntityRecord: noop,
        deleteEntityRecord: noopAsync,
        setEntityLocked: noop,
        selectEntityGroup: noop,
    };
};

/**
 * PreviewEntityProvider opens a `preview`-sourced page scope for the layout
 * currently being edited, so every leaf under it resolves to mock data
 * without touching Firestore. The mocked store is a valtio `ref`: it carries
 * class instances and writer closures that must not be proxied, and swapping
 * the whole ref on a layout edit notifies subscribed leaves atomically.
 */
export const PreviewEntityProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const editor = useContext(LayoutEditorContext);

    if (!editor) {
        throw new Error('PreviewEntityProvider must be used within a LayoutEditorProvider');
    }

    const preview = useMemo(() => ref(createPreviewScope(editor.layout)), [editor.layout]);
    const scope = usePageScopeProxy({ source: 'preview' as const, preview });

    return <PageScopeContext.Provider value={scope}>{children}</PageScopeContext.Provider>;
};
